import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Select, MenuItem, InputLabel, FormControl, Button, Divider, Alert, CircularProgress, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SettingsIcon from '@mui/icons-material/Settings';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import api from '../services/api';

export default function IAConfigPage({ currentUser }) {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState([]); // Liste des configs du backend
  const [selectedId, setSelectedId] = useState(''); // ID de la config sélectionnée
  const [config, setConfig] = useState({
    ollama: { endpoint: '', model: '' },
    openai: { apiKey: '', model: '' },
    anthropic: { apiKey: '', model: '' },
    deepseek: { apiKey: '', model: '' },
    mistral: { apiKey: '', apiUrl: '', model: '' }
  });
  const [status, setStatus] = useState({ type: null, message: '' });
  const [editingIdx, setEditingIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [iaProvider, setIaProvider] = useState('ollama');
  const [iaModel, setIaModel] = useState('llama2');
  const [customApiUrl, setCustomApiUrl] = useState('');
  const [iaConfigs, setIaConfigs] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [customApis, setCustomApis] = useState([]); // Liste des APIs IA personnalisées
  const [customApiName, setCustomApiName] = useState('');
  const [customApiUrlInput, setCustomApiUrlInput] = useState('');
  const [customApiKey, setCustomApiKey] = useState('');
  const [customApiModel, setCustomApiModel] = useState('');
  const [apiName, setApiName] = useState('');

  // Charger les configs IA globales du backend au chargement
  useEffect(() => {
    setLoading(true);
    api.get('/api/config/AI-global')
      .then(res => {
        setConfigs(res.data || []);
        setIaConfigs(res.data || []);
        if (res.data && res.data.length > 0) {
          const first = res.data[0];
          setSelectedId(first.id || first._id || '');
          setConfig(first.config || first);
          setIaProvider(first.provider || 'ollama');
          setIaModel(first.model || 'llama2');
          setCustomApiUrl(first.endpoint || '');
          setApiName(first.name || '');
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Sauvegarder une nouvelle config IA globale
  const handleSave = async () => {
    setStatus({ type: null, message: '' });
    if (!apiName || apiName.trim() === '') {
      setStatus({ type: 'error', message: t('settings.ia-config.nameRequired') || 'Le nom est requis.' });
      return;
    }
    setLoading(true);
    try {
      // Check for duplicate name
      const existing = iaConfigs.find(cfg => (cfg.name || '').toLowerCase() === apiName.trim().toLowerCase());
      // Build payload with only the selected provider's fields
      let payload = {
        name: apiName.trim(),
        provider: iaProvider,
        model: iaModel,
        endpoint: customApiUrl,
        customApis
      };
      if (iaProvider === 'openai') {
        payload.openai = config.openai;
      } else if (iaProvider === 'mistral') {
        payload.mistral = config.mistral;
      } else if (iaProvider === 'anthropic') {
        payload.anthropic = config.anthropic;
      } else if (iaProvider === 'deepseek') {
        payload.deepseek = config.deepseek;
      } else if (iaProvider === 'ollama') {
        payload.ollama = config.ollama;
      }
      if (existing) {
        // Demander confirmation avant d'écraser
        if (window.confirm(t('settings.ia-config.confirmOverwrite') || `Le nom "${apiName.trim()}" existe déjà. Écraser la configuration ?`)) {
          await api.put('/api/config/AI-global', { ...payload, id: existing.id });
          setStatus({ type: 'success', message: t('settings.ia-config.editSuccess') || 'Configuration mise à jour.' });
        } else {
          setStatus({ type: 'info', message: t('settings.ia-config.cancelled') || 'Écrasement annulé.' });
          setLoading(false);
          return;
        }
      } else {
        // Create new config (POST)
        await api.post('/api/config/AI-global', payload);
        setStatus({ type: 'success', message: t('settings.ia-config.saveSuccess') || 'Configuration enregistrée.' });
      }
      // Reload list
      const res = await api.get('/api/config/AI-global');
      setConfigs(res.data || []);
      setIaConfigs(res.data || []);
      // Clear form
      setApiName('');
      setIaProvider('ollama');
      setIaModel('llama2');
      setCustomApiUrl('');
      setConfig({
        ollama: { endpoint: '', model: '' },
        openai: { apiKey: '', model: '' },
        anthropic: { apiKey: '', model: '' },
        deepseek: { apiKey: '', model: '' },
        mistral: { apiKey: '', apiUrl: '', model: '' }
      });
      setCustomApis([]);
      setCustomApiName('');
      setCustomApiUrlInput('');
      setCustomApiKey('');
      setCustomApiModel('');
      setEditingIdx(null);
    } catch (e) {
      if (e?.response?.data?.message?.includes('duplicate')) {
        setStatus({ type: 'error', message: t('settings.ia-config.duplicateName') || 'Ce nom est déjà utilisé.' });
      } else {
        setStatus({ type: 'error', message: t('settings.ia-config.saveError') || 'Erreur lors de la sauvegarde.' });
      }
    }
    setLoading(false);
  };


  // Modifier une config IA globale (PUT)
  const handleEdit = async (idx) => {
    setStatus({ type: null, message: '' });
    setLoading(true);
    try {
      const payload = {
        name: apiName,
        provider: iaProvider,
        model: iaModel,
        endpoint: customApiUrl,
        openai: config.openai,
        anthropic: config.anthropic,
        deepseek: config.deepseek,
        mistral: config.mistral,
        customApis
      };
      await api.put('/api/config/AI-global', payload);
      setStatus({ type: 'success', message: t('settings.ia-config.editSuccess') });
      const res = await api.get('/api/config/AI-global');
      setConfigs(res.data || []);
      setIaConfigs(res.data || []);
    } catch (e) {
      setStatus({ type: 'error', message: t('settings.ia-config.editError') });
    }
    setLoading(false);
  };

  // Supprimer une config IA globale
  const handleDelete = async (idx) => {
    setStatus({ type: null, message: '' });
    setLoading(true);
    try {
      await api.delete('/api/config/AI-global');
      setStatus({ type: 'success', message: t('settings.ia-config.deleteSuccess') });
      const res = await api.get('/api/config/AI-global');
      setConfigs(res.data || []);
      setIaConfigs(res.data || []);
    } catch (e) {
      setStatus({ type: 'error', message: t('settings.ia-config.deleteError') });
    }
    setLoading(false);
  };

  // Quand l'utilisateur change de config dans la liste
  const handleSelect = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    const found = configs.find(c => c.id === id || c._id === id);
    if (found) setConfig(found.config || found);
  };

  const handleChange = (provider, field) => (e) => {
    setConfig(prev => ({
      ...prev,
      [provider]: { ...prev[provider], [field]: e.target.value }
    }));
  };

  // Ajouter une API IA personnalisée
  const handleAddCustomApi = () => {
    if (!customApiName || !customApiUrlInput) return;
    setCustomApis(prev => [...prev, {
      name: customApiName,
      url: customApiUrlInput,
      apiKey: customApiKey,
      model: customApiModel
    }]);
    setCustomApiName('');
    setCustomApiUrlInput('');
    setCustomApiKey('');
    setCustomApiModel('');
  };

  // Supprimer une API IA personnalisée
  const handleRemoveCustomApi = idx => {
    setCustomApis(prev => prev.filter((_, i) => i !== idx));
  };

  // Génère dynamiquement la liste des providers, y compris un placeholder pour la création d'une nouvelle API personnalisée
  const providerOptions = [
    { label: 'Ollama (local)', value: 'ollama' },
    { label: 'OpenAI', value: 'openai' },
    { label: 'Anthropic', value: 'anthropic' },
    { label: 'Deepseek', value: 'deepseek' },
    { label: 'Mistral', value: 'mistral' },
    { label: '+ Ajouter une IA personnalisée...', value: '__custom_new__' },
    ...customApis.map(api => ({ label: api.name || api.url, value: api.name || api.url, isCustom: true, api }))
  ];

  // Gestion de la sélection d'un provider
  const handleProviderChange = (_, v) => {
    if (!v) return;
    setIaProvider(v.value);
    if (v.value === '__custom_new__') {
      // Réinitialise les champs pour une nouvelle API personnalisée
      setCustomApiName('');
      setCustomApiUrlInput('');
      setCustomApiKey('');
      setCustomApiModel('');
    } else if (v.isCustom) {
      // Remplit les champs avec l'API personnalisée sélectionnée
      setCustomApiName(v.api.name || '');
      setCustomApiUrlInput(v.api.url || '');
      setCustomApiKey(v.api.apiKey || '');
      setCustomApiModel(v.api.model || '');
    }
  };

  return (
    <Box sx={{ p: 0, maxWidth: 800, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, background: '#fafbfc' }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <SettingsIcon color="primary" fontSize="large" />
          <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            {t('settings.ia-config.title')}
          </Typography>
        </Box>
        
        {/* Nom de l'API - Déplacé en haut du formulaire */}
        <TextField
          label={t('settings.ia-config.apiName')}
          value={apiName}
          onChange={e => setApiName(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
        />
        
        {loading && <CircularProgress sx={{ my: 4 }} />}

        <Autocomplete
          options={providerOptions}
          value={providerOptions.find(opt => opt.value === iaProvider) || providerOptions[0]}
          onChange={handleProviderChange}
          getOptionLabel={opt => opt.label}
          renderInput={params => <TextField {...params} label={t('settings.ia-config.provider')} sx={{ mb: 2 }} />}
          sx={{ mb: 2, width: 320 }}
        />
        {/* Modèle IA */}
        {providerOptions.find(opt => opt.value === iaProvider && opt.isCustom) ? (
          <>
            <TextField
              label={t('settings.ia-config.customApiUrl')}
              value={providerOptions.find(opt => opt.value === iaProvider)?.api?.url || ''}
              disabled
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('settings.ia-config.customApiKey')}
              value={providerOptions.find(opt => opt.value === iaProvider)?.api?.apiKey || ''}
              disabled
              fullWidth
              sx={{ mb: 2 }}
              type="password"
            />
            <TextField
              label={t('settings.ia-config.customApiModel')}
              value={providerOptions.find(opt => opt.value === iaProvider)?.api?.model || ''}
              disabled
              fullWidth
              sx={{ mb: 2 }}
            />
          </>
        ) : iaProvider === 'ollama' ? (
          <Autocomplete
            freeSolo
            options={['llama2', 'mistral', 'codellama']}
            value={iaModel}
            onInputChange={(_, v) => setIaModel(v)}
            renderInput={params => <TextField {...params} label={t('settings.ia-config.ollama.model')} variant="outlined" fullWidth sx={{ mb: 2 }} />}
          />
        ) : iaProvider === 'mistral' ? (
          <TextField
            select
            label={t('settings.ia-config.mistral.model')}
            value={iaModel}
            onChange={e => setIaModel(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {['mistral-tiny', 'mistral-small', 'mistral-medium'].map(model => (
              <MenuItem key={model} value={model}>{model}</MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            select
            label={t('settings.ia-config.model')}
            value={iaModel}
            onChange={e => setIaModel(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {['gpt-4', 'gpt-3.5-turbo', 'claude-3-opus', 'claude-3-sonnet', 'deepseek-chat', 'deepseek-coder'].map(model => (
              <MenuItem key={model} value={model}>{model}</MenuItem>
            ))}
          </TextField>
        )}
        {/* Paramètres spécifiques à chaque provider */}
        {iaProvider === 'openai' && (
          <TextField
            label={t('settings.ia-config.openai.apiKey')}
            value={config.openai?.apiKey || ''}
            onChange={e => setConfig(prev => ({ ...prev, openai: { ...prev.openai, apiKey: e.target.value } }))}
            fullWidth
            sx={{ mb: 2 }}
            type="password"
          />
        )}
        {iaProvider === 'anthropic' && (
          <TextField
            label={t('settings.ia-config.anthropic.apiKey')}
            value={config.anthropic?.apiKey || ''}
            onChange={e => setConfig(prev => ({ ...prev, anthropic: { ...prev.anthropic, apiKey: e.target.value } }))}
            fullWidth
            sx={{ mb: 2 }}
            type="password"
          />
        )}
        {iaProvider === 'deepseek' && (
          <TextField
            label={t('settings.ia-config.deepseek.apiKey')}
            value={config.deepseek?.apiKey || ''}
            onChange={e => setConfig(prev => ({ ...prev, deepseek: { ...prev.deepseek, apiKey: e.target.value } }))}
            fullWidth
            sx={{ mb: 2 }}
            type="password"
          />
        )}
        {iaProvider === 'mistral' && (
          <>
            <TextField
              label={t('settings.ia-config.mistral.apiKey')}
              value={config.mistral?.apiKey || ''}
              onChange={e => setConfig(prev => ({ ...prev, mistral: { ...prev.mistral, apiKey: e.target.value } }))}
              fullWidth
              sx={{ mb: 2 }}
              type="password"
            />
            <TextField
              label={t('settings.ia-config.mistral.apiUrl')}
              value={config.mistral?.apiUrl || ''}
              onChange={e => setConfig(prev => ({ ...prev, mistral: { ...prev.mistral, apiUrl: e.target.value } }))}
              fullWidth
              sx={{ mb: 2 }}
            />
          </>
        )}
        {/* URL API personnalisée */}
        <TextField
          label={t('settings.ia-config.apiUrl')}
          value={customApiUrl}
          onChange={e => setCustomApiUrl(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        {/* Si le provider sélectionné est "ajouter une IA personnalisée" ou une API custom, affiche les champs d'édition */}
        {iaProvider === '__custom_new__' || providerOptions.find(opt => opt.value === iaProvider && opt.isCustom) ? (
          <Box sx={{ mb: 2 }}>
            <TextField
              label={t('settings.ia-config.customApiName')}
              value={customApiName}
              onChange={e => setCustomApiName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('settings.ia-config.customApiUrl')}
              value={customApiUrlInput}
              onChange={e => setCustomApiUrlInput(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('settings.ia-config.customApiKey')}
              value={customApiKey}
              onChange={e => setCustomApiKey(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('settings.ia-config.customApiModel')}
              value={customApiModel}
              onChange={e => setCustomApiModel(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={handleAddCustomApi}>{t('settings.ia-config.addCustomApi')}</Button>
          </Box>
        ) : (
          // ... (le reste des champs classiques selon le provider)
          <></>
        )}
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2, width: 200 }}
          onClick={editingIdx !== null ? async () => { await handleEdit(editingIdx); setEditingIdx(null); } : handleSave}
        >
          {t('settings.ia-config.save')}
        </Button>
        {status.message && (
          <Alert severity={status.type} sx={{ mt: 2 }}>{status.message}</Alert>
        )}
        {/* Liste des configs IA enregistrées */}
        {iaConfigs.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle2">{t('settings.ia-config.savedConfigs')}</Typography>
            {iaConfigs.map((cfg, idx) => (
              <Box key={cfg.id || idx} display="flex" alignItems="center" gap={1} mb={1}>
                <Typography>{cfg.name || cfg.label || t('settings.ia-config.customApiName')}</Typography>
                <Button size="small" color="primary" variant="outlined" onClick={() => {
                  setEditingIdx(idx);
                  const cfg = iaConfigs[idx];
                  setApiName(cfg.name || '');
                  setIaProvider(cfg.provider || 'ollama');
                  setIaModel(cfg.model || '');
                  setCustomApiUrl(cfg.endpoint || '');
                  setConfig(cfg.config || cfg);
                }}>{t('settings.ia-config.edit')}</Button>
                <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => handleDelete(idx)}>{t('settings.ia-config.delete')}</Button>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
